// src/features/templates/logic.ts
// Framework-agnostic Template Hub logic — ZERO React imports.
// Pure TypeScript functions for the template hub feature.

import type {
  TemplateDefinition,
  TemplateCategory,
  TemplateScope,
  TemplateFilter,
  TemplateSortField,
} from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export interface CategoryMeta {
  id: TemplateCategory;
  labelKey: string;
  iconPath: string;
}

export const TEMPLATE_CATEGORIES: CategoryMeta[] = [
  {
    id: 'task',
    labelKey: 'templates.category.task',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
  {
    id: 'project',
    labelKey: 'templates.category.project',
    iconPath: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
  },
  {
    id: 'document',
    labelKey: 'templates.category.document',
    iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
  {
    id: 'sprint',
    labelKey: 'templates.category.sprint',
    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    id: 'workflow',
    labelKey: 'templates.category.workflow',
    iconPath: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  },
];

export interface ScopeMeta {
  id: TemplateScope;
  labelKey: string;
}

export const TEMPLATE_SCOPES: ScopeMeta[] = [
  { id: 'system', labelKey: 'templates.scope.system' },
  { id: 'organization', labelKey: 'templates.scope.organization' },
  { id: 'team', labelKey: 'templates.scope.team' },
  { id: 'personal', labelKey: 'templates.scope.personal' },
];

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/**
 * Filter templates by category, scope, and search text.
 */
export function filterTemplates(
  templates: TemplateDefinition[],
  filter: TemplateFilter,
): TemplateDefinition[] {
  let result = templates;

  if (filter.category) {
    result = result.filter(t => t.category === filter.category);
  }

  if (filter.scope) {
    result = result.filter(t => t.scope === filter.scope);
  }

  if (filter.search) {
    const query = filter.search.toLowerCase().trim();
    if (query.length > 0) {
      result = result.filter(
        t =>
          t.name.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query),
      );
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/**
 * Sort templates by the given field. Returns a new array.
 */
export function sortTemplates(
  templates: TemplateDefinition[],
  sortBy: TemplateSortField,
): TemplateDefinition[] {
  const sorted = [...templates];

  switch (sortBy) {
    case 'name':
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'usage_count':
      sorted.sort((a, b) => (b.usage_count ?? 0) - (a.usage_count ?? 0));
      break;
    case 'created_at':
      sorted.sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      });
      break;
    case 'updated_at':
      sorted.sort((a, b) => {
        const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return bTime - aTime;
      });
      break;
  }

  return sorted;
}

// ---------------------------------------------------------------------------
// Label helpers
// ---------------------------------------------------------------------------

/**
 * Returns the i18n key for a template scope.
 */
export function getTemplateScopeLabel(scope: TemplateScope): string {
  const meta = TEMPLATE_SCOPES.find(s => s.id === scope);
  return meta?.labelKey ?? 'templates.scope.unknown';
}

/**
 * Returns the i18n key for a template category.
 */
export function getTemplateCategoryLabel(category: TemplateCategory): string {
  const meta = TEMPLATE_CATEGORIES.find(c => c.id === category);
  return meta?.labelKey ?? 'templates.category.unknown';
}

/**
 * Returns the SVG icon path for a template category.
 */
export function getTemplateCategoryIcon(category: TemplateCategory): string {
  const meta = TEMPLATE_CATEGORIES.find(c => c.id === category);
  return meta?.iconPath ?? '';
}

// ---------------------------------------------------------------------------
// Scope hierarchy
// ---------------------------------------------------------------------------

const SCOPE_PRIORITY: Record<TemplateScope, number> = {
  system: 0,
  organization: 1,
  team: 2,
  personal: 3,
};

/**
 * Merge templates by scope priority: personal overrides team overrides org overrides system.
 * Templates with the same name at a higher priority scope replace lower ones.
 */
export function resolveTemplateHierarchy(
  templates: TemplateDefinition[],
  scope: TemplateScope,
): TemplateDefinition[] {
  const maxPriority = SCOPE_PRIORITY[scope];

  // Only include templates at or below the requested scope level
  const eligible = templates.filter(
    t => SCOPE_PRIORITY[t.scope] <= maxPriority,
  );

  // Group by name, keep highest priority
  const byName = new Map<string, TemplateDefinition>();

  for (const tpl of eligible) {
    const existing = byName.get(tpl.name);
    if (!existing || SCOPE_PRIORITY[tpl.scope] > SCOPE_PRIORITY[existing.scope]) {
      byName.set(tpl.name, tpl);
    }
  }

  return Array.from(byName.values());
}

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

/**
 * Check if user can edit a template.
 * Admins can edit any template. Users can edit templates they created, or personal templates.
 */
export function canEditTemplate(
  template: TemplateDefinition,
  userId: string,
  userRole: string,
): boolean {
  if (userRole === 'admin' || userRole === 'owner') return true;
  if (template.scope === 'system') return false;
  if (template.created_by === userId) return true;
  if (template.scope === 'personal') return template.created_by === userId;
  return false;
}

/**
 * Check if user can delete a template.
 * Only admins can delete system/org templates. Users can delete their own.
 */
export function canDeleteTemplate(
  template: TemplateDefinition,
  userId: string,
  userRole: string,
): boolean {
  if (userRole === 'admin' || userRole === 'owner') return true;
  if (template.scope === 'system') return false;
  if (template.scope === 'organization') return false;
  if (template.created_by === userId) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validate a template definition. Returns a map of field name to i18n error key.
 * Empty map means valid.
 */
export function validateTemplate(
  template: Partial<TemplateDefinition>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!template.name || template.name.trim().length === 0) {
    errors.name = 'templates.validation.nameRequired';
  } else if (template.name.trim().length > 200) {
    errors.name = 'templates.validation.nameTooLong';
  }

  if (!template.description || template.description.trim().length === 0) {
    errors.description = 'templates.validation.descriptionRequired';
  }

  if (!template.category) {
    errors.category = 'templates.validation.categoryRequired';
  }

  if (!template.scope) {
    errors.scope = 'templates.validation.scopeRequired';
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Badge variant mapping
// ---------------------------------------------------------------------------

export function getCategoryBadgeVariant(
  category: TemplateCategory,
): 'primary' | 'secondary' | 'warning' | 'error' {
  switch (category) {
    case 'task':
      return 'primary';
    case 'project':
      return 'secondary';
    case 'document':
      return 'warning';
    case 'sprint':
      return 'primary';
    case 'workflow':
      return 'secondary';
    default:
      return 'secondary';
  }
}

export function getScopeBadgeVariant(
  scope: TemplateScope,
): 'primary' | 'secondary' | 'warning' | 'error' {
  switch (scope) {
    case 'system':
      return 'error';
    case 'organization':
      return 'warning';
    case 'team':
      return 'primary';
    case 'personal':
      return 'secondary';
    default:
      return 'secondary';
  }
}
